import { type FocusEvent, useState } from 'react';

import useMixpanel from '../../hooks/useMixpanel';

interface UseLoginSuggestionsParams<TFieldName extends string> {
  fieldName: TFieldName;
  currentValue: string | undefined;
  recentValues: string[];
  setValue: (
    fieldName: TFieldName,
    value: string,
    options?: { shouldValidate?: boolean; shouldDirty?: boolean },
  ) => void;
  onFieldBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

export function useLoginSuggestions<TFieldName extends string>({
  fieldName,
  currentValue,
  recentValues,
  setValue,
  onFieldBlur,
}: UseLoginSuggestionsParams<TFieldName>) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { track, trackClick } = useMixpanel();

  const suggestions = currentValue
    ? recentValues.filter((value) => value.toLowerCase().includes(currentValue.toLowerCase()))
    : recentValues;
  const isOpen = showSuggestions && suggestions.length > 0;

  const selectSuggestion = (selectedValue: string) => {
    trackClick(`${fieldName}_suggestion_selected`);
    setValue(fieldName, selectedValue, { shouldValidate: true, shouldDirty: true });
    setShowSuggestions(false);
    (document.activeElement as HTMLElement)?.blur();
  };

  const onFocus = () => {
    setShowSuggestions(true);
    if (recentValues.length > 0)
      track(`${fieldName}_suggestions_shown`, { count: recentValues.length });
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    onFieldBlur?.(event);
    setTimeout(() => setShowSuggestions(false), 150);
  };

  return { suggestions, isOpen, selectSuggestion, onFocus, onBlur };
}
